import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Container } from '@mui/material';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages (lazy-loaded to reduce initial bundle size)
const Overview = lazy(() => import('./pages/Overview'));
const Achievements = lazy(() => import('./pages/Achievements'));
const Insights = lazy(() => import('./pages/Insights'));
const CarbonImpact = lazy(() => import('./pages/CarbonImpact'));
const SeasonalInsights = lazy(() => import('./pages/SeasonalInsights'));
const EfficiencyMetrics = lazy(() => import('./pages/EfficiencyMetrics'));
const QueryBuilder = lazy(() => import('./pages/QueryBuilder'));

import { useAuth } from './components/auth/AuthProvider';
import { DEFAULT_BUSINESS_ID, getBusinessId } from './services/auth';

const App = () => {
  const { logout } = useAuth();
  const businessId = getBusinessId() || DEFAULT_BUSINESS_ID;
  const location = useLocation();
  const isFullWidth = location.pathname === '/query-builder';
  const routeFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        ...(isFullWidth && { height: '100vh', maxHeight: '100vh' }),
        width: '100%',
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      <Header businessId={businessId} onLogout={logout} />
      
      {isFullWidth ? (
        /* Query Builder gets full-width, edge-to-edge layout with no padding */
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="/query-builder" element={<QueryBuilder />} />
            </Routes>
          </Suspense>
        </Box>
      ) : (
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            py: 3,
            display: 'flex',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Container maxWidth="lg">
            <Suspense fallback={routeFallback}>
              <Routes>
                <Route path="/" element={<Navigate to="/overview" />} />
                <Route path="/overview" element={<Overview businessId={businessId} />} />
                <Route path="/achievements" element={<Achievements businessId={businessId} />} />
                <Route path="/insights" element={<Insights businessId={businessId} />} />
                <Route path="/carbon-impact" element={<CarbonImpact businessId={businessId} />} />
                <Route path="/seasonal-insights" element={<SeasonalInsights />} />
                <Route path="/efficiency-metrics" element={<EfficiencyMetrics />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </Container>
        </Box>
      )}

      {!isFullWidth && <Footer />}
    </Box>
  );
};

export default App;
